import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Users, UserCheck, BarChart3, ShieldCheck, Zap,
  UserPlus, Loader, Crown, Star, User,
  Trash2, RefreshCw, MessageCircle, Send,
  Plus, Edit2, Check, X, ChevronRight, ChevronLeft,
  Eye
} from 'lucide-react';
import ErrorBoundary from '../../ErrorBoundary';
import TeamDashboard from '../TeamDashboard';
import OneOnOneSupportSystem from '../../management/OneOnOneSupportSystem';
import TeamNeurodiversityMap from '../TeamNeurodiversityMap';
import AITalentAssistantDashboard from '../../management/AITalentAssistantDashboard';
import ProjectSimulator from '../../management/ProjectSimulator';
import { radarAxes } from '../../../data/developmentLaws';
import { trizLaws } from '../../../data/trizLawDependencies';
import { SafeIcon } from '../../../contexts/nobishiro-dashboard/SVGErrorBoundary';
import { useSubscription } from '../../../hooks/useSubscription';
import { useTeam, MAX_MEMBERS_PER_GROUP } from '../../../hooks/useTeam';
import { PremiumGate } from '../../PremiumGate';
import { InviteModal } from '../../team/InviteModal';
import { db } from '../../../firebase';
import { collectionGroup, getDocs } from 'firebase/firestore';

const ROLE_COLOR = { owner: '#f59e0b', leader: '#8b5cf6', member: '#3b82f6' };
const ROLE_LABEL = { owner: 'オーナー', leader: 'リーダー', member: 'メンバー' };
const ROLE_ICON  = { owner: Crown, leader: Star, member: User };

const TeamTab = ({ currentUserProfile, allUserProfiles, teamDataLoading, isAdmin }) => {
  const [teamView, setTeamView]     = useState('groups');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteGroupId, setInviteGroupId] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null); // オーナーがドリルダウン
  const [newTeamName, setNewTeamName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);

  const { canAccessTeam, loading: subLoading } = useSubscription();
  const {
    team, groups, myGroup, myRole, myGroupId, loading: teamLoading,
    isOwner, isLeader, canManage,
    createTeam, createGroup, updateGroupName, deleteGroup,
    inviteMember, removeMember,
    sendConsultation, fetchConsultations, replyConsultation,
    refetch,
  } = useTeam();

  const tabs = [
    { id: 'groups',       label: isOwner ? 'グループ管理' : 'メンバー管理', icon: Users,         show: true },
    { id: 'teamlogs',     label: '学習ログ',      icon: BarChart3,    show: isOwner || isLeader },
    { id: 'consultation', label: '相談',          icon: MessageCircle, show: isLeader || isOwner },
    { id: 'ai_assistant', label: 'AIアシスタント', icon: ShieldCheck,  show: isOwner || isAdmin, badge: 'NEW' },
    { id: 'simulator',    label: 'プロジェクト予測', icon: Zap,         show: isOwner || isAdmin, badge: 'NEW' },
    { id: 'overview',     label: 'チーム概要',    icon: BarChart3,    show: true },
    { id: 'oneOnOne',     label: '1on1支援',      icon: UserCheck,    show: true },
  ].filter(t => t.show);

  const [inviteGroupName, setInviteGroupName] = useState('');
  const openInvite = (role, groupId, groupName = '') => {
    setInviteRole(role); setInviteGroupId(groupId); setInviteGroupName(groupName); setIsInviteModalOpen(true);
  };

  // ── オーナー：グループ一覧 ──
  const OwnerGroupList = () => {
    const groupNameRef = useRef(null);
    const handleCreateGroup = async () => {
      const name = groupNameRef.current?.value?.trim();
      if (!name) return;
      setCreatingGroup(true);
      try {
        await createGroup(name);
        if (groupNameRef.current) groupNameRef.current.value = '';
        setAddingGroup(false);
      } catch(e) { alert(e.message); }
      setCreatingGroup(false);
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* ヘッダー */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#111827', margin: '0 0 2px' }}>{team?.name}</h3>
            <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>{groups.length}グループ · 合計{groups.reduce((s, g) => s + g.members.length, 0)}人</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={refetch} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
              <RefreshCw size={13} /> 更新
            </button>
            <button onClick={() => setAddingGroup(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
              <Plus size={13} /> グループ追加
            </button>
          </div>
        </div>

        {/* グループ追加フォーム */}
        {addingGroup && (
          <div style={{ background: '#faf5ff', borderRadius: 12, padding: 20, border: '1.5px solid #e9d5ff' }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', marginBottom: 12 }}>新しいグループを作成</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                ref={groupNameRef}
                defaultValue=""
                placeholder="グループ名（例：営業チームA）"
                style={{ flex: 1, border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 13, outline: 'none', color: '#111827' }}
                onKeyDown={e => e.key === 'Enter' && handleCreateGroup()}
                autoFocus
              />
              <button onClick={handleCreateGroup} disabled={creatingGroup}
                style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {creatingGroup ? '作成中...' : '作成'}
              </button>
              <button onClick={() => { setAddingGroup(false); setNewGroupName(''); }}
                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 13, cursor: 'pointer' }}>
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* グループカード一覧 */}
        {groups.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 12, padding: '48px 24px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
            <Users size={40} color="#e0e7ff" style={{ margin: '0 auto 16px', display: 'block' }} />
            <p style={{ fontSize: 14, color: '#9ca3af' }}>まだグループがありません。「グループ追加」から作成してください。</p>
          </div>
        ) : groups.map(group => {
          const leader = group.members.find(m => m.role === 'leader');
          const members = group.members.filter(m => m.role === 'member');
          return (
            <div key={group.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              {/* グループヘッダー */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {editingGroupId === group.id ? (
                  <div style={{ display: 'flex', gap: 8, flex: 1 }}>
                    <input value={editingGroupName} onChange={e => setEditingGroupName(e.target.value)}
                      style={{ flex: 1, border: '1.5px solid #4f46e5', borderRadius: 8, padding: '6px 12px', fontSize: 14, fontWeight: 700, outline: 'none' }}
                      autoFocus onKeyDown={async e => {
                        if (e.key === 'Enter') { await updateGroupName(group.id, editingGroupName); setEditingGroupId(null); }
                      }} />
                    <button onClick={async () => { await updateGroupName(group.id, editingGroupName); setEditingGroupId(null); }}
                      style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}><Check size={14} /></button>
                    <button onClick={() => setEditingGroupId(null)}
                      style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}><X size={14} /></button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h4 style={{ fontSize: 15, fontWeight: 800, color: '#111827', margin: 0 }}>{group.name}</h4>
                    <button onClick={() => { setEditingGroupId(group.id); setEditingGroupName(group.name); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}><Edit2 size={13} /></button>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>{group.members.length}/{MAX_MEMBERS_PER_GROUP + 1}人</span>
                  <button onClick={() => setSelectedGroupId(group.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#0369a1', cursor: 'pointer' }}>
                    <Eye size={13} /> 詳細
                  </button>
                </div>
              </div>

              {/* グループ簡易表示 */}
              <div style={{ padding: '14px 20px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {/* リーダー */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#faf5ff', borderRadius: 10, border: '1px solid #e9d5ff' }}>
                  <Star size={14} color="#8b5cf6" />
                  <div>
                    <div style={{ fontSize: 10, color: '#8b5cf6', fontWeight: 700 }}>LEADER</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e1b4b' }}>{leader?.displayName || '未設定'}</div>
                  </div>
                </div>
                {/* メンバー */}
                {members.map(m => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
                    <User size={13} color="#3b82f6" />
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1e3a5f' }}>{m.displayName || '未設定'}</div>
                  </div>
                ))}
                {/* 招待ボタン */}
                {!leader && (
                  <button onClick={() => openInvite('leader', group.id, group.name)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#faf5ff', border: '1.5px dashed #c4b5fd', borderRadius: 10, fontSize: 12, fontWeight: 600, color: '#7c3aed', cursor: 'pointer' }}>
                    <Plus size={12} /> リーダー招待
                  </button>
                )}
                {members.length < MAX_MEMBERS_PER_GROUP && (
                  <button onClick={() => openInvite('member', group.id, group.name)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#eff6ff', border: '1.5px dashed #bfdbfe', borderRadius: 10, fontSize: 12, fontWeight: 600, color: '#3b82f6', cursor: 'pointer' }}>
                    <Plus size={12} /> メンバー招待
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── オーナー：グループ詳細ドリルダウン ──
  const GroupDetail = ({ groupId }) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return null;
    const leader  = group.members.find(m => m.role === 'leader');
    const members = group.members.filter(m => m.role === 'member');
    const [removing, setRemoving] = useState(null);

    const MemberCard = ({ m }) => {
      const Icon = ROLE_ICON[m.role] || User;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: '#4338ca', flexShrink: 0 }}>
            {(m.displayName || m.email || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{m.displayName || '未設定'}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{m.email}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: ROLE_COLOR[m.role] + '18', color: ROLE_COLOR[m.role], fontSize: 11, fontWeight: 700 }}>
            <Icon size={11} />{ROLE_LABEL[m.role]}
          </div>
          <button onClick={async () => {
            if (!window.confirm(`「${m.displayName}」を削除しますか？`)) return;
            setRemoving(m.id);
            try { await removeMember(groupId, m.id, m.userId); } catch(e) { alert(e.message); }
            setRemoving(null);
          }} disabled={removing === m.id}
            style={{ background: 'none', border: '1.5px solid #fecaca', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', color: '#ef4444' }}>
            <Trash2 size={13} />
          </button>
        </div>
      );
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <button onClick={() => setSelectedGroupId(null)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', fontSize: 13, fontWeight: 700, padding: 0 }}>
          <ChevronLeft size={16} /> グループ一覧に戻る
        </button>

        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>{group.name}</h3>
              <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>{group.members.length}人</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {!leader && (
                <button onClick={() => openInvite('leader', groupId, group?.name)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#faf5ff', border: '1.5px solid #8b5cf6', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, color: '#8b5cf6', cursor: 'pointer' }}>
                  <Star size={13} /> リーダー招待
                </button>
              )}
              {members.length < MAX_MEMBERS_PER_GROUP && (
                <button onClick={() => openInvite('member', groupId, group?.name)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
                  <UserPlus size={13} /> メンバー招待
                </button>
              )}
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>LEADER</div>
            {leader ? <MemberCard m={leader} /> : (
              <div style={{ padding: 16, background: '#faf5ff', borderRadius: 10, border: '1.5px dashed #c4b5fd', textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: '#7c3aed', margin: 0 }}>リーダー未設定</p>
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>MEMBERS ({members.length}/{MAX_MEMBERS_PER_GROUP})</div>
            {members.length > 0 ? members.map(m => <MemberCard key={m.id} m={m} />) : (
              <div style={{ padding: 16, background: '#eff6ff', borderRadius: 10, border: '1.5px dashed #bfdbfe', textAlign: 'center' }}>
                <p style={{ fontSize: 12, color: '#3b82f6', margin: 0 }}>メンバーを招待しましょう</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── リーダー：自分のグループのメンバー管理 ──
  const LeaderGroupView = () => {
    if (!myGroup) return (
      <div style={{ textAlign: 'center', padding: '60px 24px', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
        <p style={{ fontSize: 14, color: '#9ca3af' }}>グループに参加していません</p>
      </div>
    );
    const leader  = myGroup.members.find(m => m.role === 'leader');
    const members = myGroup.members.filter(m => m.role === 'member');
    const [removing, setRemoving] = useState(null);

    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#111827', margin: '0 0 2px' }}>{myGroup.name}</h3>
            <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>{myGroup.members.length}人</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={refetch} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
              <RefreshCw size={13} /> 更新
            </button>
            {members.length < MAX_MEMBERS_PER_GROUP && (
              <button onClick={() => openInvite('member', myGroupId, myGroup?.name)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
                <UserPlus size={13} /> メンバー招待
              </button>
            )}
          </div>
        </div>

        {/* リーダー（自分） */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>LEADER（あなた）</div>
          {leader && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: '#7c3aed' }}>
                {(leader.displayName || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{leader.displayName}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{leader.email}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: '#f5f3ff', color: '#8b5cf6', fontSize: 11, fontWeight: 700 }}>
                <Star size={11} />リーダー
              </div>
            </div>
          )}
        </div>

        {/* メンバー */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', marginBottom: 4 }}>MEMBERS ({members.length}/{MAX_MEMBERS_PER_GROUP})</div>
          {members.length === 0 ? (
            <div style={{ padding: 20, background: '#eff6ff', borderRadius: 10, border: '1.5px dashed #bfdbfe', textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: '#3b82f6', margin: 0 }}>メンバーを招待しましょう</p>
            </div>
          ) : members.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: '#2563eb' }}>
                {(m.displayName || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{m.displayName || '未設定'}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{m.email}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: '#eff6ff', color: '#3b82f6', fontSize: 11, fontWeight: 700 }}>
                <User size={11} />メンバー
              </div>
              <button onClick={async () => {
                if (!window.confirm(`「${m.displayName}」を削除しますか？`)) return;
                setRemoving(m.id);
                try { await removeMember(myGroupId, m.id, m.userId); } catch(e) { alert(e.message); }
                setRemoving(null);
              }} disabled={removing === m.id}
                style={{ background: 'none', border: '1.5px solid #fecaca', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', color: '#ef4444' }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 20, padding: '12px 16px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd' }}>
          <p style={{ fontSize: 12, color: '#0369a1', margin: 0 }}>💡 このグループのメンバーのみ管理できます。他のグループは閲覧できません。</p>
        </div>
      </div>
    );
  };

  // ── 学習ログタブ ──
  const TeamLogsTab = () => {
    const [logs, setLogs]     = useState([]);
    const [loadingL, setLoadingL] = useState(true);
    const [period, setPeriod] = useState('month');

    const CATEGORIES = [
      { id: 'reading',  label: '読書・インプット',    emoji: '📚', color: '#3b82f6' },
      { id: 'practice', label: '実践・アウトプット',  emoji: '⚡', color: '#f59e0b' },
      { id: 'research', label: '調査・リサーチ',      emoji: '🔍', color: '#8b5cf6' },
      { id: 'meeting',  label: '議論・ミーティング',  emoji: '💬', color: '#10b981' },
      { id: 'creation', label: '制作・クリエイティブ', emoji: '🎨', color: '#ef4444' },
      { id: 'review',   label: '振り返り・復習',      emoji: '🔄', color: '#6366f1' },
      { id: 'other',    label: 'その他',              emoji: '📝', color: '#6b7280' },
    ];

    const toDate = (v) => {
      if (!v) return null;
      try {
        if (v?.toDate) return v.toDate();
        if (v?.seconds) return new Date(v.seconds * 1000);
        const d = new Date(v); return isNaN(d.getTime()) ? null : d;
      } catch { return null; }
    };

    // 閲覧可能なメンバーUIDを計算
    const visibleUids = isOwner
      ? groups.flatMap(g => g.members.map(m => m.userId)).filter(Boolean)
      : (myGroup?.members.map(m => m.userId).filter(Boolean) || []);

    // 表示用メンバー情報
    const visibleMembers = isOwner
      ? groups.flatMap(g => g.members.map(m => ({ ...m, groupName: g.name })))
      : (myGroup?.members || []);

    useEffect(() => {
      if (!team || !visibleUids.length) { setLoadingL(false); return; }
      (async () => {
        setLoadingL(true);
        try {
          const snap = await getDocs(collectionGroup(db, 'logs'));
          const all  = snap.docs
            .map(d => ({ id: d.id, uid: d.ref.parent.parent?.id || '', ...d.data() }))
            .filter(l => visibleUids.includes(l.uid));
          setLogs(all);
        } catch(e) { console.error(e); }
        setLoadingL(false);
      })();
    }, [team, myGroupId, groups.length]);

    const filteredLogs = logs.filter(l => {
      const d = toDate(l.date || l.createdAt);
      if (!d) return false;
      const now = new Date();
      if (period === 'week') { const w = new Date(now); w.setDate(w.getDate()-7); return d >= w; }
      if (period === 'month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      return true;
    });

    const totalHours   = filteredLogs.reduce((s, l) => s + Number(l.hours || 0), 0);
    const avgQuality   = (() => {
      const r = filteredLogs.filter(l => l.quality_rating > 0);
      return r.length > 0 ? (r.reduce((s,l) => s + l.quality_rating, 0) / r.length).toFixed(1) : '0.0';
    })();

    const memberStats = visibleMembers.map(m => {
      const ml = filteredLogs.filter(l => l.uid === m.userId);
      return { ...m, hours: Math.round(ml.reduce((s,l) => s + Number(l.hours||0), 0)*10)/10, count: ml.length };
    }).sort((a,b) => b.hours - a.hours);
    const maxH = Math.max(...memberStats.map(m => m.hours), 1);

    const catStats = CATEGORIES.map(c => {
      const cl = filteredLogs.filter(l => l.category === c.id);
      return { ...c, hours: Math.round(cl.reduce((s,l) => s + Number(l.hours||0), 0)*10)/10 };
    }).sort((a,b) => b.hours - a.hours);
    const maxC = Math.max(...catStats.map(c => c.hours), 1);

    const recentLogs = [...filteredLogs]
      .sort((a,b) => (toDate(b.date||b.createdAt)||0) - (toDate(a.date||a.createdAt)||0))
      .slice(0, 10)
      .map(l => {
        const m = visibleMembers.find(m => m.userId === l.uid);
        const cat = CATEGORIES.find(c => c.id === l.category) || CATEGORIES[6];
        return { ...l, memberName: m?.displayName || '不明', groupName: m?.groupName || '', cat };
      });

    if (loadingL) return <div style={{ textAlign: 'center', padding: 60 }}><Loader size={24} color="#9ca3af" /></div>;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#111827', margin: 0 }}>
            {isOwner ? '📊 全グループ学習ログ' : `📊 ${myGroup?.name || ''} 学習ログ`}
          </h3>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{id:'week',label:'今週'},{id:'month',label:'今月'},{id:'all',label:'全期間'}].map(p => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: period===p.id ? '#4f46e5' : '#f3f4f6', color: period===p.id ? '#fff' : '#374151' }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
          {[
            { label: '総学習時間', value: `${Math.round(totalHours*10)/10}h`, icon: '⏱️', color: '#3b82f6' },
            { label: '総ログ件数', value: `${filteredLogs.length}件`, icon: '📝', color: '#10b981' },
            { label: 'アクティブ', value: `${new Set(filteredLogs.map(l=>l.uid)).size}人`, icon: '🔥', color: '#ef4444' },
            { label: '平均品質', value: `${avgQuality}/5`, icon: '⭐', color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: 16, border: `1px solid ${s.color}30`, borderTop: `3px solid ${s.color}`, textAlign: 'center' }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #e5e7eb' }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 16 }}>🏆 メンバー別学習時間</h4>
            {memberStats.map((m, i) => (
              <div key={m.id || i} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: ['#f59e0b','#9ca3af','#b45309','#e5e7eb','#e5e7eb'][i]||'#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: i < 3 ? '#fff' : '#6b7280' }}>{i+1}</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{m.displayName||'未設定'}</div>
                      {isOwner && m.groupName && <div style={{ fontSize: 10, color: '#9ca3af' }}>{m.groupName}</div>}
                    </div>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#3b82f6' }}>{m.hours}h</span>
                </div>
                <div style={{ height: 6, background: '#f3f4f6', borderRadius: 999 }}>
                  <div style={{ height: 6, width: `${(m.hours/maxH)*100}%`, background: 'linear-gradient(90deg,#4f46e5,#7c3aed)', borderRadius: 999 }} />
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #e5e7eb' }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 16 }}>📚 カテゴリ別</h4>
            {catStats.map(c => (
              <div key={c.id} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                  <span>{c.emoji} {c.label}</span>
                  <span style={{ fontWeight: 700, color: c.color }}>{c.hours}h</span>
                </div>
                <div style={{ height: 6, background: '#f3f4f6', borderRadius: 999 }}>
                  <div style={{ height: 6, width: `${(c.hours/maxC)*100}%`, background: c.color, borderRadius: 999 }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #e5e7eb' }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 16 }}>📋 直近の学習ログ</h4>
          {recentLogs.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '24px 0' }}>ログがありません</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['日付','メンバー', isOwner ? 'グループ' : null,'カテゴリ','時間','品質'].filter(Boolean).map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentLogs.map(l => (
                    <tr key={l.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', fontSize: 11, color: '#6b7280' }}>
                        {(() => { const d = toDate(l.date||l.createdAt); return d ? d.toLocaleDateString('ja-JP',{month:'short',day:'numeric'}) : '-'; })()}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#111827' }}>{l.memberName}</td>
                      {isOwner && <td style={{ padding: '10px 12px', fontSize: 11, color: '#6b7280' }}>{l.groupName}</td>}
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, background: l.cat.color+'18', color: l.cat.color, fontWeight: 600 }}>{l.cat.emoji} {l.cat.label}</span>
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: '#3b82f6' }}>{Number(l.hours).toFixed(1)}h</td>
                      <td style={{ padding: '10px 12px', color: '#f59e0b' }}>{l.quality_rating > 0 ? '★'.repeat(l.quality_rating) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── 相談タブ ──
  const ConsultationTab = () => {
    const [consultations, setConsultations] = useState([]);
    const [loadingC, setLoadingC] = useState(true);
    const [title, setTitle]   = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent]     = useState(false);
    const [replyText, setReplyText] = useState({});
    const [replying, setReplying]   = useState(null);

    useEffect(() => {
      (async () => {
        setLoadingC(true);
        const list = await fetchConsultations();
        setConsultations(list);
        setLoadingC(false);
      })();
    }, []);

    const handleSend = async () => {
      if (!title.trim() || !message.trim()) return;
      setSending(true);
      try {
        await sendConsultation(title.trim(), message.trim());
        setSent(true); setTitle(''); setMessage('');
        const list = await fetchConsultations();
        setConsultations(list);
        setTimeout(() => setSent(false), 3000);
      } catch(e) { alert(e.message); }
      setSending(false);
    };

    const handleReply = async (cId) => {
      const text = replyText[cId];
      if (!text?.trim()) return;
      setReplying(cId);
      try {
        await replyConsultation(cId, text.trim());
        const list = await fetchConsultations();
        setConsultations(list);
        setReplyText(prev => ({ ...prev, [cId]: '' }));
      } catch(e) { alert(e.message); }
      setReplying(null);
    };

    const fmt = (v) => {
      try { const d = v?.toDate?.() || new Date(v); return d.toLocaleDateString('ja-JP',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}); } catch { return ''; }
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {isLeader && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MessageCircle size={18} color="#8b5cf6" /> オーナーへ相談する
            </h3>
            <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20 }}>グループ「{myGroup?.name}」に関する相談をオーナーに送れます</p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>相談タイトル</label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="例：新しいメンバー採用について"
                style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 13, outline: 'none', color: '#111827', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>相談内容</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
                placeholder="相談したい内容を詳しく書いてください..."
                style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <button onClick={handleSend} disabled={sending || !title.trim() || !message.trim()}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: !title.trim() || !message.trim() ? '#e9d5ff' : 'linear-gradient(135deg,#8b5cf6,#6d28d9)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              <Send size={15} />{sending ? '送信中...' : '相談を送る'}
            </button>
            {sent && <p style={{ color: '#059669', fontSize: 13, marginTop: 12 }}>✅ 相談を送信しました！</p>}
          </div>
        )}

        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 20 }}>
            {isOwner ? '📬 受信した相談' : '📋 送信した相談'}
          </h3>
          {loadingC ? <div style={{ textAlign: 'center', padding: 32 }}><Loader size={24} color="#9ca3af" /></div>
          : consultations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
              <MessageCircle size={36} color="#e5e7eb" style={{ margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: 13 }}>まだ相談はありません</p>
            </div>
          ) : consultations.map(c => (
            <div key={c.id} style={{ marginBottom: 16, padding: 20, background: '#f8fafc', borderRadius: 12, border: `1px solid ${c.status==='replied'?'#bbf7d0':'#e5e7eb'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>{c.title}</h4>
                  {isOwner && c.groupName && <p style={{ fontSize: 11, color: '#8b5cf6', margin: 0 }}>グループ：{c.groupName}</p>}
                </div>
                <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 700, background: c.status==='replied'?'#dcfce7':'#fef3c7', color: c.status==='replied'?'#166534':'#92400e' }}>
                  {c.status==='replied'?'✅ 返信済み':'⏳ 返信待ち'}
                </span>
              </div>
              <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 8 }}>{c.message}</p>
              <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>{c.fromName} · {fmt(c.createdAt)}</p>
              {c.reply && (
                <div style={{ marginTop: 12, padding: '12px 16px', background: '#ecfdf5', borderRadius: 10, borderLeft: '3px solid #10b981' }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: '#059669', marginBottom: 4 }}>オーナーからの返信</p>
                  <p style={{ fontSize: 13, color: '#065f46', margin: 0, lineHeight: 1.6 }}>{c.reply}</p>
                </div>
              )}
              {isOwner && c.status !== 'replied' && (
                <div style={{ marginTop: 12 }}>
                  <textarea value={replyText[c.id]||''} onChange={e => setReplyText(prev => ({...prev,[c.id]:e.target.value}))} rows={3}
                    placeholder="返信を入力..." style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
                  <button onClick={() => handleReply(c.id)} disabled={replying===c.id || !replyText[c.id]?.trim()}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    <Send size={13} />{replying===c.id?'送信中...':'返信する'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── グループタブ（振り分け） ──
  const GroupsTab = () => {
    if (isOwner) {
      return selectedGroupId
        ? <GroupDetail groupId={selectedGroupId} />
        : <OwnerGroupList />;
    }
    return <LeaderGroupView />;
  };

  const teamFilteredProfiles = allUserProfiles
    ? Object.fromEntries(
        Object.entries(allUserProfiles).filter(([uid]) =>
          groups.flatMap(g => g.members.map(m => m.userId)).includes(uid)
        )
      )
    : {};

  const renderContent = () => {
    if (teamLoading) return <div style={{ textAlign: 'center', padding: 48 }}><Loader size={32} color="#9ca3af" /></div>;
    switch(teamView) {
      case 'groups':       return <GroupsTab />;
      case 'teamlogs':     return <TeamLogsTab />;
      case 'consultation': return <ConsultationTab />;
      case 'ai_assistant': return <div className="bg-white rounded-xl shadow-sm p-6"><AITalentAssistantDashboard userProfiles={teamFilteredProfiles} /></div>;
      case 'simulator':    return <div className="bg-white rounded-xl shadow-sm p-6"><ProjectSimulator userProfiles={teamFilteredProfiles} /></div>;
      case 'overview':     return (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6"><TeamDashboard userProfiles={teamFilteredProfiles} radarAxes={radarAxes} trizLaws={trizLaws} /></div>
          <div className="bg-white rounded-xl shadow-sm p-6"><TeamNeurodiversityMap userProfiles={teamFilteredProfiles} /></div>
        </div>
      );
      case 'oneOnOne':     return <div className="bg-white rounded-xl shadow-sm p-6"><OneOnOneSupportSystem managerProfile={currentUserProfile} memberProfiles={teamFilteredProfiles} /></div>;
      default:             return <GroupsTab />;
    }
  };

  if (subLoading) return <div style={{ textAlign: 'center', padding: 48 }}><Loader size={32} /></div>;
  if (!canAccessTeam) return (
    <div className="relative min-h-[400px]">
      <PremiumGate title="チーム機能" description="Businessプランでチーム機能をご利用いただけます。" recommendedPlan="business" />
    </div>
  );

  // チーム未作成
  if (!team && !teamLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 40, maxWidth: 420, width: '100%', textAlign: 'center', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Users size={48} color="#c7d2fe" style={{ margin: '0 auto 16px', display: 'block' }} />
        <h3 style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 8 }}>チームを作成しましょう</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 28 }}>チーム名を設定し、グループを自由に作成できます。</p>
        <input value={newTeamName} onChange={e => setNewTeamName(e.target.value)} placeholder="チーム名（例：富永コーチングチーム）"
          style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', fontSize: 14, outline: 'none', marginBottom: 16, boxSizing: 'border-box', color: '#111827' }}
          onKeyDown={e => e.key === 'Enter' && !isCreating && newTeamName.trim() && (setIsCreating(true), createTeam(newTeamName.trim()).finally(() => setIsCreating(false)))} />
        <button onClick={async () => { if (!newTeamName.trim()) return; setIsCreating(true); try { await createTeam(newTeamName.trim()); } catch(e) { alert(e.message); } setIsCreating(false); }}
          disabled={!newTeamName.trim() || isCreating}
          style={{ width: '100%', background: !newTeamName.trim()||isCreating ? '#c7d2fe' : 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 15, fontWeight: 700, cursor: !newTeamName.trim()||isCreating ? 'not-allowed' : 'pointer' }}>
          {isCreating ? '作成中...' : 'チームを作成する 🚀'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '12px 16px', border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setTeamView(t.id); if (t.id === 'groups') setSelectedGroupId(null); }}
              style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: teamView===t.id ? '#4f46e5' : '#f3f4f6', color: teamView===t.id ? '#fff' : '#374151' }}>
              <t.icon size={14} />{t.label}
              {t.badge && <span style={{ position: 'absolute', top: -8, right: -8, fontSize: 10, background: '#ef4444', color: '#fff', padding: '1px 6px', borderRadius: 20 }}>{t.badge}</span>}
            </button>
          ))}
        </div>
      </div>
      <ErrorBoundary>{renderContent()}</ErrorBoundary>
      <InviteModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} inviteRole={inviteRole} groupId={inviteGroupId} groupName={inviteGroupName} />
    </div>
  );
};

export default TeamTab;
